import { CircularProgress, Box, Drawer, useMediaQuery } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  clearNewTempPermitData,
  getPermitPackages,
  getSavedProjects,
} from "../../../store/actions/dataActions";
import OpenDocuments from "../../noc/OpenDocuments";
import { useTour } from "@reactour/tour";
import NotarizationRequestsGrid from "../../permitComponents/NotarizationRequestsGrid";

import {
  clearMyPermitState,
  setPermitLoadings,
  setModalsState,
  setRightValue,
  setRowCount,
  setSelectedRow,
  setDocumentImage,
  setCreatingDocTitle,
} from "../../../store/actions/uiActions/myPermitsActions";
// import MyPermitGrid from "../../permitComponents/MyPermitGrid";

const NotarizationRequests = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const permitPackages = useSelector((state) => state.nocData?.permitPackages);
  const newTempPermitData = useSelector(
    (state) => state.nocData?.newTempPermitData
  );
  const { dataLoading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings
  );

  const { permitDrawerOpen } = useSelector(
    (state) => state.myPermitsComponent.permits.modalStates
  );

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [newPermitOpened, setNewPermitOpened] = useState(false);

  const [docNotaryTypeValue, setDocNotaryTypeValue] = useState(0);

  const { isOpen, currentStep, setCurrentStep } = useTour();

  const hasFetchedOnceRef = useRef(false);

  const handlePermitDrawerClose = () => {
    dispatch(setModalsState({ permitDrawerOpen: false }));
  };

  useEffect(() => {
    return () => {
      dispatch(clearMyPermitState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (newTempPermitData && !newPermitOpened) {
      const newRow = newTempPermitData;

      if (!isOpen) {
        setNewPermitOpened(true);
        dispatch(setSelectedRow(newRow));
        dispatch(setCreatingDocTitle("Notice of Commencement"));

        dispatch(setDocumentImage(newRow?.selected_documents?.[0]?.img));
        dispatch(setModalsState({ permitDrawerOpen: true }));
      }

      dispatch(clearNewTempPermitData());
    }

    const fetchData = async () => {
      if (hasFetchedOnceRef.current) return;

      hasFetchedOnceRef.current = true;

      await dispatch(setPermitLoadings({ dataLoading: true }));
      await dispatch(getSavedProjects());
      const result = await dispatch(
        getPermitPackages({
          customActiveTab: 3,
          sortDirection: "desc",
          sortField: "requested_at",
        }),
      );

      dispatch(setRowCount(result?.data || 0));
      setLoading(false);
      await dispatch(setCreatingDocTitle("Notice of Commencement"));
      await dispatch(setPermitLoadings({ dataLoading: false }));
    };

    fetchData();
  }, [dispatch, isOpen, newPermitOpened, newTempPermitData]);

  useEffect(() => {
    const correctStep = currentSession === "subUserSession" ? 20 : 30;
    const nextStep = currentSession === "subUserSession" ? 10 : 12;
    // 10 / 12
    if (
      isOpen &&
      currentStep === correctStep &&
      permitPackages.length > 0 &&
      !loading &&
      !dataLoading
    ) {
      setTimeout(() => {
        setCurrentStep((s) => nextStep);
      }, 200);
    }
  }, [
    permitPackages,
    currentStep,
    isOpen,
    loading,
    currentSession,
    setCurrentStep,
    dataLoading,
  ]);

  useEffect(() => {
    const correctStep = currentSession === "userSession" ? 21 : 21;

    if (
      isOpen &&
      currentStep === correctStep &&
      permitPackages.length > 0 &&
      !loading &&
      !dataLoading
    ) {
      setSelectedRow(permitPackages[0]);
      setTimeout(() => {
        setRightValue(5);
      }, 200);
    }
  }, [
    permitPackages,
    currentStep,
    isOpen,
    loading,
    currentSession,
    dispatch,
    dataLoading,
  ]);

  useEffect(() => {
    if (currentUser) {
      if (currentUser?.user?.expired === true) {
        navigate("/subscription");
      }
    }
  }, [currentUser, navigate]);

  const memoizedDrawerContent = useMemo(() => <OpenDocuments />, []);

  const memoizedGridSection = useMemo(
    () => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          px: mobileSize ? 0 : "1.5rem",
          py: mobileSize ? 0 : "1.5rem",
        }}
      >
        <Box sx={{ maxWidth: "1920px", width: "100%", height: "100%" }}>
          <Box
            sx={{
              borderRadius: mobileSize ? 0 : "1.5rem",
              boxShadow: "2px 3px 5px #d3d3d3",
              height: {
                xs: "100%",
                xl: "100%",
              },
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
              }}
            >
              <NotarizationRequestsGrid
                setDocNotaryTypeValue={setDocNotaryTypeValue}
                docNotaryTypeValue={docNotaryTypeValue}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    ),
    [docNotaryTypeValue, mobileSize],
  );

  return (
    <>
      {loading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: mobileSize ? "0px" : "1.5rem",
          }}
        >
          <Box sx={{ maxWidth: "1920px", width: "100%", height: "100%" }}>
            <Box
              sx={{
                borderRadius: mobileSize ? 0 : "1.5rem",
                boxShadow: "2px 3px 5px #d3d3d3",
                minHeight: "100%",
                ...(mobileSize
                  ? {
                      height: "100%",
                      maxHeight: "100%",
                    }
                  : null),
                backgroundColor: "#fff",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                // p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  height: "100%",
                }}
              >
                {memoizedGridSection}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Drawer
        PaperProps={{
          sx: {
            width: "100vw",
          },
        }}
        anchor="right"
        open={permitDrawerOpen}
        onClose={handlePermitDrawerClose}
      >
        {memoizedDrawerContent}
      </Drawer>
    </>
  );
};

export default React.memo(NotarizationRequests);
